import { trackPageView as _trackPageView } from '@snowplow/browser-tracker';
import debounce from 'lodash/debounce';
import {
  type PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

import { TRACKER_IDS } from 'lib/analytics/constants';

import type { SnowplowPageViewContextType } from '../types';

const PAGEVIEW_DEBOUNCE = 500;
const trackPageView = debounce(_trackPageView, PAGEVIEW_DEBOUNCE);

export type SnowplowPageViewContextValueType = {
  pause: () => void;
  resume: () => void;
  setContext: React.Dispatch<React.SetStateAction<SnowplowPageViewContextType>>;
};
const SnowplowPageViewContext = createContext<
  SnowplowPageViewContextValueType | undefined
>(undefined);

export const SnowplowProvider = ({ children }: PropsWithChildren) => {
  const location = useLocation();
  const [context, setContext] = useState<SnowplowPageViewContextType>(null);

  const [isPaused, setPaused] = useState(false);
  const pageViewContextRef = useRef({
    pause: () => setPaused(true),
    resume: () => setPaused(false),
    setContext,
  });

  useEffect(() => {
    if (isPaused) {
      trackPageView.cancel();
    } else {
      trackPageView({ context }, [TRACKER_IDS.sp.default]);
    }
  }, [isPaused, context, location.pathname, location.search]);

  return (
    <SnowplowPageViewContext.Provider
      value={pageViewContextRef.current}
      children={children}
    />
  );
};

export const useSnowplowPageViewContext = () => {
  const value = useContext(SnowplowPageViewContext);
  if (!value) {
    throw new Error(
      'useSnowplowPageViewContext() must be called within a <SnowplowProvider />',
    );
  }
  return value;
};

export default SnowplowProvider;
